import clsx from 'clsx'
import {useState, useEffect} from 'react'
import {ID, useDebounce, KTIcon} from '@/_metronic/helpers'
import {Product} from '../../../core/_models'
import {getSupplierProducts} from '../../../../suppliers/core/_requests'
import {SupplierProduct} from '../../../../suppliers/core/_models'
import {useFormikContext} from 'formik'

type Props = {
  supplierId: ID
  product: Product
}

const Lookup: React.FC<Props>= ({supplierId, product}) => {
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)
  const [products, setProducts] = useState<Array<SupplierProduct>>([])
  const [selected, setSelected] = useState<string | undefined>()
  const {setFieldValue, setTouched} = useFormikContext()
  const debouncedSearchTerm = useDebounce(searchTerm, 150)
  const search = async () => {
    if (debouncedSearchTerm !== undefined && searchTerm.length >= 3) {
      setLoading(true)
      setProducts(await getSupplierProducts(
        supplierId,
        debouncedSearchTerm,
        product.region,
        product.type === 'TYPE_GIFT_CARD' ? product.faceAmount : undefined,
        product.type === 'TYPE_GIFT_CARD' ? product.faceCurrency : undefined,
      ))
      setLoading(false)
    }
  }

  useEffect(() => {
    search()
  }, [debouncedSearchTerm])

  const select = async (p: SupplierProduct) => {
    setSelected(p.sku)
    await setFieldValue('name', p.name)
    await setFieldValue('sku', p.sku)
    await setFieldValue('pricing', {
      discount: 0,
      amount: p.amount,
      currency: 'EUR'
    })
    setTouched({
      name: true,
      sku: true,
      pricing: {
        discount: true,
        currency: true,
        amount: true
      }
    })
  }

  return (
    <>
      <div className='separator separator-dashed my-5'></div> 
      <div className="position-relative mb-3">
        <KTIcon
          iconName='magnifier'
          className='fs-2 text-lg-1 text-gray-500 position-absolute top-50 translate-middle-y ms-0'
        />

        <input
          name="search"
          type="text"
          placeholder="Search product ..."
          className="form-control form-control-flush ps-10"
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          disabled={loading}
        />

        {loading && (
          <span
            className='position-absolute top-50 end-0 translate-middle-y lh-0 me-1'
            data-kt-search-element='spinner'
          >
            <span className='spinner-border h-15px w-15px align-middle text-gray-400' />
          </span>
        )}
        {searchTerm && !loading && (
          <span
            className='btn btn-flush btn-active-color-primary position-absolute top-50 end-0 translate-middle-y lh-0'
            data-kt-search-element='clear'
            onClick={() => {
              setSearchTerm('')
              setProducts([])
            }}
          >
            <KTIcon iconName='cross' className='fs-2 text-lg-1 me-0' />
          </span>
        )}
      </div>
      <div className="row">
        {searchTerm.length > 0 && !loading && products.length === 0 && (
          <div className="col-12 text-danger text-center">
            No product found
          </div>
        )}
        {products.map((p: SupplierProduct) => (
          <div className="col-6 mb-5">
            <div
              className={clsx(
                "btn btn-outline btn-outline-dashed btn-outline-default p-7 d-flex align-items-center",
                { active: p.sku === selected }
              )}
              onClick={() => select(p)}>
              {p.name}
            </div>
          </div>
        ))}
      </div>
      <div className='separator separator-dashed my-5'></div> 
    </>
  )
}

export {Lookup}
